import {effect, inject, Injectable} from '@angular/core'
import {BetType} from '@/api/player-api/models/bet-type'
import {BetslipRiskInfoDto} from '@/api/player-api/models/betslip-risk-info-dto'
import {CreateBetslipDto} from '@/api/player-api/models/create-betslip-dto'
import {CreateBetslipSelectionDto} from '@/api/player-api/models/create-betslip-selection-dto'
import {BetslipRiskService} from '@/features/betslip/betslip-create/risk/betslip-risk.service'
import {MarketOutcome} from './demo-offer'
import {LiabilityLimitsState, PlayerLiabilityTier} from './liability-limits.state'
import {ceilingFor, forVipPlayer, LiabilityLimit, NO_CEILING, tightestLimit, wholeBetslipCeiling} from './mock-betslip-risk-backend'

/**
 * How a feed market maps onto the workbench's liability table (the demo offer's market ids).
 * An outcome is found by its odds id where the feed keeps those the same on every match, and by
 * its (English) outcome name where it numbers them per match.
 */
interface FeedMarket {
	marketId: number
	byOddsId?: Record<number, string>
	byOutcomeName?: Record<string, string>
}

/** Checked on dev-cloud data (MLS, 2026-10-14). Any other market gets no ceiling. */
const FEED_MARKETS: Record<number, FeedMarket> = {
	// 3-way → Full Time Result
	1: {marketId: 1, byOddsId: {1: 'home', 2: 'draw', 3: 'away'}},
	// Double chance → Double Chance
	10: {marketId: 6, byOddsId: {4: '1x', 5: '12', 6: 'x2'}},
	// Both teams to score → Both Teams To Score
	29: {marketId: 5, byOutcomeName: {yes: 'yes', no: 'no'}},
	// Total goals: one limit for every line, as the workbench sets it (the selection carries no line).
	18: {marketId: 3, byOutcomeName: {over: 'over', under: 'under'}}
}

/** The workbench pair a feed Selection is priced against, if it has one. */
export function feedPair(dto: CreateBetslipSelectionDto): Pick<MarketOutcome, 'marketId' | 'outcomeKey'> | undefined {
	const market = FEED_MARKETS[dto.marketId]
	const outcomeKey = market?.byOddsId?.[dto.oddsId]
		?? market?.byOutcomeName?.[dto.outcomeName?.trim().toLowerCase()]

	return market && outcomeKey ? {marketId: market.marketId, outcomeKey} : undefined
}

/** The Betslip Types whose stake is one for the whole Betslip, so a ceiling for it applies. */
const WHOLE_BETSLIP_TYPES = [BetType.single, BetType.combo, BetType.betBuilderCombo]

/**
 * The risk reply for a normal or a VIP player. `upperLimit` is the Auto-accept tier, a stake above
 * it going for Authorization; `vipUpperLimit` is the cap. A normal player is accepted automatically
 * up to the Limit column and capped at the VIP column; a VIP up to the VIP column and not capped,
 * which `forVipPlayer` makes of the normal player's reply.
 *
 * One ceiling per Selection, each priced on its own odds against its own pair, whatever the Betslip
 * Type. A Single, a Combo and a Bet Builder Combo also get one for the whole Betslip: never priced
 * on combined odds, it is the smallest of their Selections' ceilings.
 */
export function mockSelectionLiability(
	limits: LiabilityLimit[],
	payload: CreateBetslipDto,
	tier: PlayerLiabilityTier.normal | PlayerLiabilityTier.vip
): BetslipRiskInfoDto {
	const selections = (payload.selections ?? []).flatMap((dto) => {
		const pair = feedPair(dto)
		if (!pair) {
			return []
		}

		return [{
			matchId: dto.matchId,
			oddsId: dto.oddsId,
			problems: [],
			riskLimitDto: {
				upperLimit: ceilingFor(dto.odds, tightestLimit(limits, [pair], 'liabilityLimit')),
				vipUpperLimit: ceilingFor(dto.odds, tightestLimit(limits, [pair], 'vipLiabilityLimit')),
				upperPayLimit: 0
			}
		}]
	})

	const reply: BetslipRiskInfoDto = {
		problemCode: 0,
		problems: [],
		riskLimitDto: WHOLE_BETSLIP_TYPES.includes(payload.betType)
			? wholeBetslipCeiling(selections.map((s) => s.riskLimitDto))
			: NO_CEILING,
		selections,
		systems: []
	}

	return tier === PlayerLiabilityTier.vip ? forVipPlayer(reply) : reply
}

/**
 * Dev builds only: answers the player's betslip risk requests with per-Selection liability
 * ceilings from the workbench's Liability tab until the back end sends real ones, for the player
 * the workbench picks; "Not applied" leaves the betslip to the back end. Started by
 * DevToolsComponent, which production builds swap for a no-op.
 */
@Injectable({providedIn: 'root'})
export class PlayerLiabilityMock {

	constructor() {
		const state = inject(LiabilityLimitsState)
		const risk = inject(BetslipRiskService)

		// A new source for every edit, so the player's betslip is priced again right away.
		effect(() => {
			const limits = state.limits()
			const tier = state.playerTier()

			risk.devSource.set(tier === PlayerLiabilityTier.off
				? undefined
				: (payload) => mockSelectionLiability(limits, payload, tier))
		})
	}
}
